<script setup lang="ts">
/**
 * Глобальная навигация DUSTAR.PRO.
 * Десктоп (≥760px): sticky-хедер 72px с пунктами на всю высоту и активным
 * подчёркиванием акцентом. Мобильный (<760px): хедер 60px с гамбургером,
 * полноэкранное меню и нижняя таб-панель из 4 пунктов.
 * Брейкпоинт — CSS (min-[760px]/max-[759px]), а не JS-подписка на resize:
 * то же переключение, что в прототипе (window.innerWidth < 760), но без
 * лишнего listener'а и рассинхрона при первой отрисовке.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { X } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useNavPalette } from '@/composables/useNavPalette'
import { useNavMenuItems } from '@/composables/useNavMenuItems'
import { useI18n } from '@/composables/useI18n'
import { useLanguage } from '@/composables/useLanguage'
import { SITE_NAME } from '@/composables/useSeo'
import api from '@/services/api'
import LanguageSwitcher from './LanguageSwitcher.vue'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const { open: openPalette } = useNavPalette()
const { items: menuItems } = useNavMenuItems()
const { t } = useI18n()
const { localize, basePath, current: language } = useLanguage()

/**
 * Пункты навигации. Адреса локализуются под текущий язык: со страницы
 * /en/blog ссылка «Projects» обязана вести на /en/projects, иначе читатель
 * молча выпадает обратно в русскую версию.
 */
const desktopNavItems = computed(() => [
  { label: t('nav.projects'), to: localize('/projects') },
  { label: t('nav.media'), to: localize('/media') },
  { label: t('nav.blog'), to: localize('/blog') },
])

/** Нижняя таб-панель на мобильном — свой набор (см. github.md/прототип). */
const mobileTabs = computed(() => [
  { label: t('nav.home'), to: localize('/') },
  { label: t('nav.projects'), to: localize('/projects') },
  { label: t('nav.media'), to: localize('/media') },
  { label: t('nav.blog'), to: localize('/blog') },
])

/**
 * Активность считаем по пути без языкового префикса — иначе на /en/blog
 * ни один пункт не подсветится.
 */
function isActive(path: string): boolean {
  const target = path.replace(/^\/(en|zh)(?=\/|$)/, '') || '/'
  const here = basePath.value
  return here === target || (target !== '/' && here.startsWith(target + '/'))
}

function goHome(): void {
  router.push(localize('/'))
}

async function onLogout(): Promise<void> {
  await auth.logout()
  router.push('/')
}

/**
 * Секретик: наведение на логотип перебирает статус-строки.
 *
 * Числа здесь были захардкожены («24 записи, 6 проектов, сборка 41, 3 трека»)
 * и с реальностью не сходились — сайт публично показывал выдуманную статистику.
 * Теперь берутся из /api/stats. Пока цифры не приехали, показываем название
 * сайта, а не заглушку с числом.
 */
const siteStats = ref<{ articles: { published: number }; categories: number; tags: number } | null>(null)

const hints = computed<string[]>(() => {
  const s = siteStats.value
  if (!s) return [SITE_NAME]

  return [
    countLabel(s.articles.published, ['запись', 'записи', 'записей'], ['entry', 'entries'], '篇'),
    countLabel(s.categories, ['раздел', 'раздела', 'разделов'], ['section', 'sections'], '个板块'),
    countLabel(s.tags, ['тег', 'тега', 'тегов'], ['tag', 'tags'], '个标签'),
  ]
})

const hintIndex = ref(0)
const logoHint = computed(() => hints.value[hintIndex.value % hints.value.length])
function cycleHint(): void {
  hintIndex.value += 1
}

/** Русский склоняет, английский считает единственное/множественное, китайский не склоняет. */
function countLabel(n: number, ru: [string, string, string], en: [string, string], zh: string): string {
  if (language.value === 'ru') {
    const m10 = n % 10
    const m100 = n % 100
    const form = m10 === 1 && m100 !== 11 ? ru[0]
      : m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20) ? ru[1]
      : ru[2]
    return `${n} ${form}`
  }
  if (language.value === 'zh') return `${n} ${zh}`
  return `${n} ${n === 1 ? en[0] : en[1]}`
}

onMounted(async () => {
  // Статистика — украшение, а не условие работы шапки: молча переживаем отказ
  try {
    const { data } = await api.get('/stats')
    siteStats.value = data
  } catch {
    siteStats.value = null
  }
})

/** Мобильное полноэкранное меню. */
const mobileMenuOpen = ref(false)
function toggleMobileMenu(): void {
  mobileMenuOpen.value = !mobileMenuOpen.value
}
function goAndCloseMenu(to: string): void {
  mobileMenuOpen.value = false
  router.push(to)
}
watch(
  () => route.fullPath,
  () => {
    mobileMenuOpen.value = false
  },
)
watch(mobileMenuOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})
</script>

<template>
  <!-- Десктоп: sticky-хедер 72px -->
  <header class="app-header-desktop hidden min-[760px]:block">
    <div class="app-header-desktop-inner">
      <div class="app-logo" @click="goHome" @mouseenter="cycleHint">
        <span class="app-logo-text">DUSTAR.PRO</span>
        <span class="app-logo-dot" aria-hidden="true"></span>
        <span class="kicker">{{ logoHint }}</span>
      </div>

      <nav class="app-nav">
        <RouterLink
          v-for="item in desktopNavItems"
          :key="item.to"
          :to="item.to"
          class="app-nav-item"
          :class="{ 'app-nav-item-active': isActive(item.to) }"
        >
          {{ item.label }}
        </RouterLink>
      </nav>

      <div class="flex items-center gap-2">
        <button
          type="button"
          class="btn btn-secondary btn-icon app-palette-btn"
          title="Быстрая навигация (/)"
          @click="openPalette"
        >
          /
        </button>
        <LanguageSwitcher />
        <!-- Кнопки «Войти» в шапке нет: вход не публичный элемент интерфейса.
             Владелец попадает в админку через палитру `/` или прямым адресом
             /admin-login. Для уже вошедшего профиль и выход остаются. -->
        <template v-if="auth.isAuthenticated">
          <RouterLink to="/profile" class="btn btn-ghost">{{ auth.userName }}</RouterLink>
          <button type="button" class="btn btn-secondary" @click="onLogout">Выйти</button>
        </template>
      </div>
    </div>
  </header>

  <!-- Мобильный: хедер 60px + гамбургер -->
  <header class="app-header-mobile min-[760px]:hidden">
    <div class="app-header-mobile-inner">
      <div class="app-logo app-logo-mobile" @click="goHome">
        <span class="app-logo-text app-logo-text-mobile">DUSTAR.PRO</span>
        <span class="app-logo-dot-mobile" aria-hidden="true"></span>
      </div>
      <!-- На мобильном переключатель нужен в самой шапке: до полноэкранного
           меню читатель может и не добраться, а язык менять надо сразу. -->
      <div class="app-header-mobile-actions">
        <LanguageSwitcher />
        <button
          type="button"
          class="app-hamburger"
          :aria-expanded="mobileMenuOpen"
          aria-label="Открыть меню"
          @click="toggleMobileMenu"
        >
          <span class="app-hamburger-bar"></span>
          <span class="app-hamburger-bar"></span>
        </button>
      </div>
    </div>
  </header>

  <!-- Полноэкранное мобильное меню -->
  <div v-if="mobileMenuOpen" class="app-mobile-menu min-[760px]:hidden">
    <div class="app-mobile-menu-top">
      <span class="kicker">навигация</span>
      <button type="button" class="app-mobile-menu-close" aria-label="Закрыть меню" @click="toggleMobileMenu">
        <X :size="20" :stroke-width="2" />
      </button>
    </div>
    <div class="app-mobile-menu-list">
      <button
        v-for="item in menuItems"
        :key="item.to"
        type="button"
        class="app-mobile-menu-item"
        @click="goAndCloseMenu(item.to)"
      >
        {{ item.label }}
      </button>
    </div>
  </div>

  <!-- Нижняя таб-панель (мобильный, всегда поверх контента) -->
  <nav class="app-tabbar min-[760px]:hidden" aria-label="Основная навигация">
    <RouterLink
      v-for="tab in mobileTabs"
      :key="tab.to"
      :to="tab.to"
      class="app-tab"
      :class="{ 'app-tab-active': isActive(tab.to) }"
    >
      <span class="app-tab-dot" aria-hidden="true"></span>
      {{ tab.label }}
    </RouterLink>
  </nav>
</template>

<style scoped>
/* Переключатель языка и гамбургер стоят рядом в мобильной шапке */
.app-header-mobile-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* — десктоп — */
.app-header-desktop {
  position: sticky;
  top: 0;
  z-index: 80;
  background: var(--color-bg);
  border-bottom: 2px solid var(--color-divider);
}
.app-header-desktop-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-8);
  height: 72px;
  display: flex;
  align-items: center;
  gap: var(--space-8);
}
.app-logo {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  margin-right: auto;
  cursor: pointer;
}
.app-logo-text {
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: 19px;
  letter-spacing: -0.015em;
}
.app-logo-dot {
  width: 8px;
  height: 8px;
  background: var(--color-accent);
  display: block;
  animation: app-logo-breathe 5s ease-in-out infinite;
}
@keyframes app-logo-breathe {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .app-logo-dot {
    animation: none;
    opacity: 1;
  }
}

.app-nav {
  display: flex;
  align-items: stretch;
  height: 72px;
}
.app-nav-item {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 var(--space-4);
  font-size: 14px;
  color: var(--color-neutral-700);
  text-decoration: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color 0.3s ease;
}
.app-nav-item:hover {
  color: var(--color-text);
}
.app-nav-item-active {
  font-family: var(--font-heading);
  font-weight: 800;
  color: var(--color-accent-700);
  border-bottom-color: var(--color-accent);
}
.app-palette-btn {
  justify-content: center;
  font-family: var(--font-mono);
}

/* — мобильный хедер — */
.app-header-mobile {
  position: sticky;
  top: 0;
  z-index: 80;
  background: var(--color-bg);
  border-bottom: 2px solid var(--color-divider);
}
.app-header-mobile-inner {
  padding: 0 var(--space-4);
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.app-logo-mobile {
  gap: var(--space-2);
}
.app-logo-text-mobile {
  font-size: 17px;
}
.app-logo-dot-mobile {
  width: 7px;
  height: 7px;
  background: var(--color-accent);
  display: block;
}
.app-hamburger {
  width: 44px;
  height: 44px;
  margin-right: -10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: none;
  border: 0;
  cursor: pointer;
}
.app-hamburger-bar {
  width: 18px;
  height: 2px;
  background: var(--color-text);
  display: block;
}

/* — полноэкранное меню — */
.app-mobile-menu {
  position: fixed;
  inset: 0;
  z-index: 120;
  background: var(--color-bg);
  display: flex;
  flex-direction: column;
}
/* Как и у таб-панели: скрываем медиа-запросом, иначе scoped display: flex
   перевесит утилиту и меню накроет весь экран на десктопе. */
@media (min-width: 760px) {
  .app-mobile-menu {
    display: none;
  }
}
.app-mobile-menu-top {
  padding: 0 var(--space-4);
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid var(--color-divider);
}
.app-mobile-menu-close {
  width: 44px;
  height: 44px;
  margin-right: -10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--color-text);
}
.app-mobile-menu-list {
  flex: 1;
  padding: var(--space-6) var(--space-4);
  overflow-y: auto;
}
.app-mobile-menu-item {
  width: 100%;
  text-align: left;
  padding: var(--space-4) 0;
  background: none;
  border: 0;
  border-bottom: 1px solid var(--color-divider);
  color: var(--color-text);
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: 22px;
  letter-spacing: -0.02em;
  cursor: pointer;
}

/* — нижняя таб-панель — */
/* Нижняя таб-панель — только на мобильном. Медиа-запрос здесь, а не утилитой
   Tailwind: scoped-селектор весит больше, чем .hidden, и перебивал бы её. */
.app-tabbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
  background: var(--color-bg);
  border-top: 2px solid var(--color-divider);
  display: flex;
}
@media (min-width: 760px) {
  .app-tabbar {
    display: none;
  }
}
.app-tab {
  flex: 1;
  height: 58px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-right: 1px solid var(--color-divider);
  text-decoration: none;
  color: var(--color-neutral-600);
  font-size: 11px;
}
.app-tab:last-child {
  border-right: 0;
}
.app-tab-dot {
  width: 6px;
  height: 6px;
  background: transparent;
  display: block;
}
.app-tab-active {
  font-family: var(--font-heading);
  font-weight: 800;
  color: var(--color-accent-700);
}
.app-tab-active .app-tab-dot {
  background: var(--color-accent);
}
</style>
